'use client';

import { PersonalLoanFilterWrapper } from './wrapper';
import type {
  CmsArticle,
  CmsBaseComponent,
  CmsPage,
} from '$/(cms)/_modules/cmsService/types';
import { hasLoanFilterHost, type LoanTerms } from '$/(cms)/_modules/loanScope';

/**
 * The reader's loan filter on a page that has no module supplying one
 * (CMS-1890).
 *
 * Until now the filter appeared only as a side effect of a Comparison Table or
 * Selected Products being present, so editors added a module to a page just to
 * expose it. With page-level terms the calculation no longer needs those
 * modules, and whether the reader gets the widget is stated rather than
 * inferred.
 *
 * Silent unless the page says so: the flag defaults to false, because an editor
 * who fixed an amount and tenure on the page has not thereby asked to let the
 * reader change them.
 */
export const PageLoanFilter = ({
  page,
  components,
  terms,
}: {
  page: Pick<CmsPage | CmsArticle, 'section' | 'show_loan_filter'>;
  /** Flattened, in page order. */
  components: CmsBaseComponent[];
  /** What the page scope calculates with right now — the engine's answer. */
  terms: LoanTerms;
}) => {
  // A block that mounts its own filter puts it into the same portal — whether
  // or not its loan fields are filled; the page widget yields to it.
  if (
    page.section !== 'PERSONAL_LOANS' ||
    !page.show_loan_filter ||
    hasLoanFilterHost(components)
  ) {
    return null;
  }

  return (
    <PersonalLoanFilterWrapper
      vertical="PERSONAL_LOANS"
      // What the page calculates with right now. The widget writes the reader's
      // edits back to the URL, and the page scope picks them up from there.
      defaultParams={{
        amount: String(terms.amount),
        tenure: String(terms.tenure),
      }}
      onFinishedEnterFilter={() => undefined}
      min_loan_amount={undefined}
      max_loan_amount={undefined}
      min_loan_tenure={undefined}
      max_loan_tenure={undefined}
    />
  );
};
